import React, { useEffect, useState } from 'react';
import withComponentAuth from '../../hoc/withComponentAuth';
import { Box, Typography } from '@mui/material';
import { SmartFilter } from '../../components/common/SmartFilter';
import { CreateTableHeader } from '../../components/common/TableHeader';
import {
  headerMaterialTable,
  headerServicesTable,
} from '../../utils/constants';
import { GETServices } from '../../services/service';
import Pagination from '../../components/atoms/Pagination';
import { ServiceList } from '../../components/ui/list/Service';
import { IMaterial, IService } from '../../utils/entities/interface';
import { useNavigate } from 'react-router-dom';
import { debounce } from '../../utils/helper';
import { MaterialList } from '../../components/ui/list/Material';
import {
  CreateMaterial,
  EditMaterial,
  GETMaterial,
} from '../../services/material';
import AddMaterialModal from './AddMaterialModal';
import useSnackbar from '../../hooks/useSnackbar';

interface FormData {
  id?: string;
  name: string;
  price: string;
  unit: string;
  status: string;
}

const MaterialTable = () => {
  const { SnackbarComponent, showSnackbar } = useSnackbar();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [material, setMaterial] = useState<IMaterial[]>([]);
  const [totalMaterial, setTotalMaterial] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [addMaterial, setAddMaterial] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    id: '',
    name: '',
    price: '',
    unit: '',
    status: '',
  });

  useEffect(() => {
    fetchMaterial(page, rowsPerPage, searchQuery);
  }, [page, rowsPerPage, searchQuery]);

  // Fetch users from API
  const fetchMaterial = async (
    page: number,
    limit: number,
    searchQuery?: string,
    sort?: 'ASC' | 'DESC',
  ) => {
    try {
      setIsLoading(true);
      const response = await GETMaterial({
        page,
        limit,
        searchQuery,
        sortOrder: sort,
      });
      setMaterial(response.data);
      setTotalMaterial(response.pagination.totalRecords);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const refeshList = () => {
    fetchMaterial(page, rowsPerPage, searchQuery);
  };

  const handleSaveRefresh = () => {
    refeshList();
  };

  // Debounced search handler
  const handleSearchRefresh = debounce((search: string) => {
    setSearchQuery(search);
    setPage(1);
  }, 500);

  const handleFilterRefresh = () => {
    console.log('FILTER HAPPENED');
  };

  //   -----------------Calling API for add material-------------------
  async function handleSubmit() {
    if (formData.id) {
      await EditMaterial({
        name: formData.name,
        rate: formData.price,
        unit: formData.unit,
        status: formData.status,
        materialId: formData.id,
      }).then((response) => {
        if (response.status) {
          showSnackbar('Material Edited Successfully', 'success');
          setAddMaterial(false);
          fetchMaterial(1, 10, '');
          setFormData({
            id: '',
            name: '',
            price: '',
            unit: '',
            status: '',
          });
        }
      });
    } else {
      await CreateMaterial({
        name: formData.name,
        rate: formData.price,
        unit: formData.unit,
        status: formData.status,
      }).then((response) => {
        if (response.status == 201) {
          showSnackbar('Material Added Successfully', 'success');
          setAddMaterial(false);
          fetchMaterial(1, 10, '');
          setFormData({
            id: '',
            name: '',
            price: '',
            unit: '',
            status: '',
          });
        }
      });
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      {SnackbarComponent}
      <AddMaterialModal
        open={addMaterial}
        onClose={() => {
          setAddMaterial(false);
          setFormData({
            id: '',
            name: '',
            price: '',
            unit: '',
            status: '',
          });
        }}
        onSaved={() => {
          handleSubmit();
        }}
        setFormData={setFormData}
        formData={formData}
      />
      <SmartFilter
        title="Meterial"
        createFilter={['SEARCH', 'ADD']}
        filterHeader={'Filter Material'}
        onNavigate={() => {
          setAddMaterial(true);
        }}
        onSaved={handleSaveRefresh}
        addDrawerSize="lg"
        onFiltered={handleFilterRefresh}
        onSearch={handleSearchRefresh}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          p: 1,
        }}
      >
        <Box
          sx={{
            height: '100%',
            maxHeight: '100%',
            overflowY: 'scroll',
            scrollbarWidth: 'none',
          }}
        >
          {material.length <= 0 && !isLoading ? (
            <Typography textAlign={'center'} marginTop={'20%'}>
              No Material Found
            </Typography>
          ) : (
            <MaterialList
              headerCells={headerMaterialTable}
              material={material}
              isLoading={isLoading}
              setAddMaterial={setAddMaterial}
              setFormData={setFormData}
            />
          )}
        </Box>
        <Box display={'block'} bottom={0} right={0}>
          <Pagination
            count={totalMaterial}
            page={page - 1}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default withComponentAuth(MaterialTable);
